import { Button, Checkbox, Flex, SimpleGrid, useMediaQuery } from '@chakra-ui/react'
import { faPlus } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import React, { type ChangeEvent, type ReactElement } from 'react'

import CourseQ from '@/queries/CourseQ'
import TopicFollowQ from '@/queries/TopicFollowQ'
import TopicQ from '@/queries/TopicQ'
import { useUser } from '@/client/context/user'
import useAPIWrapper from '@/client/hooks/useAPIWrapper'
import { type Query, SQL } from '@/client/query'
import { RPCRefresh } from '@/client/rpc'

import RequireLoginAndApproval from '@/components/Auth/RequireLoginAndApproval'
import { largeScreen } from '@/components/UI/common.styled'

import CourseFollowModal from './CourseFollowModal'
import CourseFollowPopover from './CourseFollowPopover'

export default function CourseFollowButton({ course }: { course: Query }): JSX.Element | null {
  const user = useUser()

  const [isLgPlus] = useMediaQuery(`(min-width: ${largeScreen})`)
  const [popoverOpen, setPopoverOpen] = React.useState(false)
  const [buttonIsDisabled, setButtonIsDisabled] = React.useState(false)

  const topics = course.many(CourseQ.getTopics({ excludeChallenge: true }))
  const courseName = course(CourseQ.name)

  const followedTopicIds = course.many`${TopicQ.where({
    courseId: CourseQ.id,
    id: SQL`ANY(SELECT ${TopicFollowQ.topic_id} ${TopicFollowQ.where({ userId: user.id })})`,
  })}`.map(topic => topic(TopicQ.id) ?? 0)

  const [followSelection, setFollowSelection] = React.useState(new Set(followedTopicIds))

  const handleCheckboxClick = React.useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const topicId = Number.parseInt(e.target.value, 10)
      if (e.target.checked && !followSelection.has(topicId)) {
        const addedSet = new Set(followSelection)
        setFollowSelection(addedSet.add(topicId))
      } else {
        const deletedSet = new Set(followSelection)
        deletedSet.delete(topicId)
        setFollowSelection(deletedSet)
      }
    },
    [followSelection]
  )

  const [handleSubmit] = useAPIWrapper(
    async () => {
      setPopoverOpen(false)
      setButtonIsDisabled(true)

      // To-be-followed topics: not in old follow data but  in new list
      const follow = Array.from(followSelection).filter(
        topicId => !followedTopicIds.includes(topicId)
      )

      // To-be-unfollowed topics: in old follow data but not in new list
      const unfollow = Array.from(followedTopicIds).filter(topicId => !followSelection.has(topicId))

      await RPCRefresh.modifyTopicFollow({
        followTopicIds: follow,
        unfollowTopicIds: unfollow,
      })

      setButtonIsDisabled(false)
      setPopoverOpen(false)
    },
    [followSelection, followedTopicIds],
    'Modify Topic Follow'
  )

  const FollowTopicBody = (): ReactElement => (
    <SimpleGrid columns={2} spacingX="8px">
      {topics.map(topic => {
        const topicId = topic(TopicQ.id) ?? 0
        return (
          <Checkbox
            value={topicId}
            key={topicId ?? 0}
            p={2}
            spacing="12px"
            isChecked={followSelection.has(topicId)}
            onChange={handleCheckboxClick}
            borderRadius="md"
            color={followSelection.has(topicId) ? 'primary.fg' : 'fg'}
            _hover={{
              bgColor: 'bg.subtle',
            }}
            _disabled={{
              color: 'fg.subtle',
              cursor: 'not-allowed',
            }}
          >
            <Flex alignItems="center">{topic(TopicQ.name)}</Flex>
          </Checkbox>
        )
      })}
    </SimpleGrid>
  )

  const followButton = (
    <RequireLoginAndApproval>
      <Button
        leftIcon={<FontAwesomeIcon icon={faPlus} />}
        colorScheme="blue"
        variant={followedTopicIds.length === 0 ? 'solid' : 'outline'}
        onClick={() => {
          if (!buttonIsDisabled) {
            setFollowSelection(new Set(followedTopicIds))
            setPopoverOpen(!popoverOpen)
          }
        }}
        width={['100%', 'auto']}
        isDisabled={buttonIsDisabled}
      >
        {followedTopicIds.length === 0
          ? 'Follow'
          : followedTopicIds.length === topics.length
            ? 'Following All Topics'
            : `Following (${followedTopicIds.length}) Topic${
                followedTopicIds.length > 1 ? 's' : ''
              }`}
      </Button>
    </RequireLoginAndApproval>
  )

  return isLgPlus ? (
    <CourseFollowPopover
      followButton={followButton}
      FollowTopicBody={FollowTopicBody}
      popoverIsOpen={popoverOpen}
      setPopoverIsOpen={setPopoverOpen}
      courseName={courseName}
      onSubmit={handleSubmit}
    />
  ) : (
    <CourseFollowModal
      followButton={followButton}
      FollowTopicBody={FollowTopicBody}
      modalIsOpen={popoverOpen}
      setModalIsOpen={setPopoverOpen}
      courseName={courseName}
      onSubmit={handleSubmit}
    />
  )
}
